/**
 * 柱状图组件
 *
 * 使用 ECharts 实现的柱状图，用于展示分类数据对比
 */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart as EChartsBarChart, BarSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { AggregatedDataItem } from '@/types/analysis';
import { useThemeStore } from '@/stores/themeStore';
import { getCommonEChartsTheme } from '@/utils/chartTheme';
import './BarChart.css';

echarts.use([
  EChartsBarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

interface BarChartProps {
  data: AggregatedDataItem[];
  height?: number;
  title?: string;
  loading?: boolean;
  horizontal?: boolean; // 是否横向显示
}

export function BarChart({ data, height = 400, title, loading = false, horizontal = false }: BarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    // 只在实例不存在时初始化
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 检查实例是否已被销毁
    if (!chart || chart.isDisposed()) return;

    const labels = data.map((item) => item.label);
    const values = data.map((item) => item.value);

    const commonTheme = getCommonEChartsTheme(theme);
    const titleColor = theme === 'dark' ? '#FFFFFF' : '#1E293B';
    const labelColor = theme === 'dark' ? '#9CA3AF' : '#5C5C5C';

    const option: echarts.EChartsCoreOption = {
      title: {
        text: title,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: titleColor,
        },
      },
      tooltip: {
        ...commonTheme.tooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          const percentage = data[param.dataIndex]?.percentage;
          return `${param.axisValue}<br/>数量: <strong>${param.value}</strong>${
            percentage !== undefined ? `<br/>占比: ${percentage}%` : ''
          }`;
        },
      },
      grid: {
        ...commonTheme.grid,
        bottom: data.length > 10 ? '15%' : '10%',
        top: title ? '15%' : '10%',
      },
      xAxis: horizontal
        ? {
            type: 'value',
            ...commonTheme.xAxis,
          }
        : {
            type: 'category',
            data: labels,
            ...commonTheme.xAxis,
            axisLabel: { ...commonTheme.xAxis.axisLabel, rotate: labels.length > 10 ? 45 : 0 },
          },
      yAxis: horizontal
        ? {
            type: 'category',
            data: labels,
            ...commonTheme.yAxis,
          }
        : {
            type: 'value',
            ...commonTheme.yAxis,
          },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: '60%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#4096FF' },
              { offset: 1, color: '#1677FF' },
            ]),
            borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
          },
          label: {
            show: data.length <= 20,
            position: horizontal ? 'right' : 'top',
            formatter: '{c}',
            color: labelColor,
            fontSize: 12,
          },
        } as BarSeriesOption,
      ],
      dataZoom: data.length > 10 ? [
        {
          type: 'inside',
          start: 0,
          end: data.length > 20 ? 50 : 100,
        },
        {
          type: 'slider',
          start: 0,
          end: data.length > 20 ? 50 : 100,
          height: 20,
          bottom: 5,
        },
      ] : undefined,
    };

    chart.setOption(option, { notMerge: false });

    const handleResize = () => {
      if (chart && !chart.isDisposed()) {
        chart.resize();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, loading, horizontal, theme]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="bar-chart-loading" style={{ height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bar-chart-empty" style={{ height }}>
        <div className="empty-message">暂无数据</div>
      </div>
    );
  }

  return <div ref={chartRef} className="bar-chart" style={{ height }} />;
}
